/**
 * CommandRegistry.ts - Outlinr Command Registry Manager
 * 
 * CHANGE HISTORY:
 * - 251115_2230 [pending] danielbeer: Create command registry manager using Registr
 * 
 * Manages command registration with Commandr using Registr for centralized definitions.
 */

import { createCommandHandlers, type OutlinerContext } from './handlers';
import COMMAND_REGISTRY from '../../COMMAND_REGISTRY.json';

// Commandr manager type (any for now - will be typed properly in React component)
type CommandrManager = any;

export interface CommandDefinition {
  id: string;
  category: string;
  label: string;
  description: string;
  keys: string[];
  scope: string;
  preventDefault: boolean;
  allowInInput: boolean;
  handler: string;
}

export interface CommandCategory {
  label: string;
  description: string;
  icon: string;
}

/**
 * OutlinerCommandRegistry
 * 
 * Manages command registration for Outlinr using:
 * - Registr: Centralized command definitions
 * - Commandr: Keyboard shortcut management
 */
export class OutlinerCommandRegistry {
  private commandr: CommandrManager;
  private context: OutlinerContext;
  private handlers: ReturnType<typeof createCommandHandlers>;
  private registeredCommands: Map<string, () => void> = new Map();
  
  constructor(commandr: CommandrManager, context: OutlinerContext) {
    this.commandr = commandr;
    this.context = context;
    this.handlers = createCommandHandlers(context);
  }
  
  /**
   * Register all commands from registry
   */
  registerAll(): void {
    // Set scope to outliner
    this.commandr.setScope('outliner');
    
    // Register each command
    Object.entries(COMMAND_REGISTRY.commands).forEach(([id, config]: [string, any]) => {
      this.registerCommand(id, config);
    });
    
    // console.log(`Registered ${this.registeredCommands.size} outliner commands`);
  }
  
  /**
   * Register a single command
   */
  private registerCommand(id: string, config: any): void {
    const handlerName = config.handler;
    const handler = (this.handlers as any)[handlerName];
    
    if (!handler) {
      console.warn(`Handler not found for command: ${id} (${handlerName})`);
      return;
    }
    
    // Register each key binding separately
    const unregisterFns: (() => void)[] = [];
    
    config.keys.forEach((key: string) => {
      const unregister = this.commandr.register(
        key,
        handler,
        {
          scope: config.scope,
          description: config.description,
          preventDefault: config.preventDefault,
          allowInInput: config.allowInInput
        }
      );
      unregisterFns.push(unregister);
    });
    
    // Store combined unregister function
    this.registeredCommands.set(id, () => {
      unregisterFns.forEach(fn => fn());
    });
  }
  
  /**
   * Unregister all commands
   */
  unregisterAll(): void {
    this.registeredCommands.forEach((unregister, id) => {
      unregister();
    });
    this.registeredCommands.clear();
    
    // Reset scope
    this.commandr.setScope('global');
  }
  
  /**
   * Get all commands
   */
  getCommands(category?: string): CommandDefinition[] {
    const commands = Object.entries(COMMAND_REGISTRY.commands)
      .map(([id, cmd]: [string, any]) => ({
        id,
        ...cmd
      }));
    
    if (category) {
      return commands.filter(cmd => cmd.category === category);
    }
    
    return commands;
  }
  
  /**
   * Get command by ID
   */
  getCommand(id: string): CommandDefinition | undefined {
    const cmd = (COMMAND_REGISTRY.commands as any)[id];
    if (!cmd) return undefined;
    
    return {
      id,
      ...cmd
    };
  }
  
  /**
   * Get all categories
   */
  getCategories(): Record<string, CommandCategory> {
    return COMMAND_REGISTRY.categories as Record<string, CommandCategory>;
  }
  
  /**
   * Get category
   */
  getCategory(name: string): CommandCategory | undefined {
    return (COMMAND_REGISTRY.categories as any)[name];
  }
  
  /**
   * Check if command is registered
   */
  isRegistered(id: string): boolean {
    return this.registeredCommands.has(id);
  }
  
  /**
   * Get registry metadata
   */
  getMetadata(): any {
    return COMMAND_REGISTRY.metadata;
  }
  
  /**
   * Export command list for documentation
   */
  exportCommandList(): string {
    let output = '# Outlinr Commands\n\n';
    
    const categories = this.getCategories();
    
    Object.entries(categories).forEach(([key, category]) => {
      output += `## ${category.label}\n\n`;
      output += `${category.description}\n\n`;
      
      const commands = this.getCommands(key);
      
      commands.forEach(cmd => {
        output += `### ${cmd.label}\n`;
        output += `**Keys**: ${cmd.keys.join(', ')}\n\n`;
        output += `${cmd.description}\n\n`;
      });
      
      output += '\n';
    });
    
    return output;
  }
  
  /**
   * Generate keyboard shortcut cheat sheet
   */
  generateCheatSheet(): Record<string, any[]> {
    const cheatSheet: Record<string, any[]> = {};
    
    const categories = this.getCategories();
    
    Object.entries(categories).forEach(([key, category]) => {
      const commands = this.getCommands(key);
      
      cheatSheet[category.label] = commands.map(cmd => ({
        label: cmd.label,
        keys: cmd.keys,
        description: cmd.description
      }));
    });
    
    return cheatSheet;
  }
}
